import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Typography
} from '@mui/material';
import {
  Speed as SpeedIcon,
  Sync as SyncIcon
} from '@mui/icons-material';
import {
  getBriefingPerformanceSyncStatus,
  triggerBriefingPerformanceSync
} from '../../services/apiService';
import BriefingPerformanceFilterAdmin from './BriefingPerformanceFilterAdmin';

const darkGreenColor = '#1D4434';
const POLL_MS = 3000;
// The sync starts in the background; if the status never moves after this long, say so instead of waiting forever
const GIVE_UP_MS = 30000;

const plural = (n, word) => `${n} ${word}${n !== 1 ? 's' : ''}`;

const toMs = (iso) => {
  const time = iso ? new Date(iso).getTime() : NaN;
  return Number.isNaN(time) ? 0 : time;
};

/** Latest server-side time the status moved: a run finished, or a run failed. Compared with itself, never with this browser's clock. */
const progressMarker = (status) => Math.max(toMs(status && status.lastSyncAt), toMs(status && status.lastError && status.lastError.at));

const formatAsOf = (iso) => {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const day = date.toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'
  });
  const time = date.toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', timeZone: 'UTC'
  });
  return `${day}, ${time} UTC`;
};

const clippedMonths = (list) => (Array.isArray(list) ? list : [])
  .map((item) => (typeof item === 'string' ? item : item?.yearMonth))
  .filter(Boolean);

const clippedTooltip = (list) => (Array.isArray(list) ? list : [])
  .map((item) => {
    if (typeof item === 'string') return item;
    const ym = item?.yearMonth;
    if (!ym) return '';
    return item.reason ? `${ym} (${item.reason})` : ym;
  })
  .filter(Boolean)
  .join('; ');

/** What to tell the admin once a run they started has reported back. */
const outcomeNotice = (status, baseline) => {
  const ranSince = toMs(status.lastSyncAt) > baseline;
  const failedSince = toMs(status.lastError && status.lastError.at) > baseline;
  const months = plural(status.lastSyncCount ?? 0, 'month');
  if (failedSince && !ranSince) {
    return { severity: 'error', text: `Sync failed: ${status.lastError.message}` };
  }
  if (failedSince) {
    return { severity: 'warning', text: `Sync finished with errors — ${months} upserted. ${status.lastError.message}` };
  }
  return { severity: 'success', text: `Sync complete — ${months} upserted.` };
};

/**
 * Admin → Ops Center → Vital Signs Config → Client Performance sync.
 * Manual Datadog → Mongo monthly rollup for briefing Client Performance reports.
 */
const BriefingPerformanceSyncAdmin = () => {
  const [syncStatus, setSyncStatus] = useState(null);
  const [waiting, setWaiting] = useState(false);
  const [notice, setNotice] = useState(null);
  // Set while a run this page started has not reported back: where the status stood before, and when it started
  const pendingRef = useRef(null);

  /** Reads the status, keeps it on screen and returns it (null when it could not be read). */
  const fetchSyncStatus = useCallback(async () => {
    try {
      const res = await getBriefingPerformanceSyncStatus();
      if (!res?.success) return null;
      setSyncStatus(res.data);
      return res.data;
    } catch (_) {
      return null; // the status strip is best-effort
    }
  }, []);

  const checkProgress = useCallback(async () => {
    const status = await fetchSyncStatus();
    const pending = pendingRef.current;
    if (!status || !pending || status.isRunning) return;
    if (progressMarker(status) > pending.baseline) {
      pendingRef.current = null;
      setWaiting(false);
      setNotice(outcomeNotice(status, pending.baseline));
    } else if (Date.now() - pending.startedAt > GIVE_UP_MS) {
      pendingRef.current = null;
      setWaiting(false);
      setNotice({ severity: 'error', text: 'The sync did not report back. Check the server log.' });
    }
  }, [fetchSyncStatus]);

  useEffect(() => { fetchSyncStatus(); }, [fetchSyncStatus]);

  const busy = Boolean(syncStatus?.isRunning || waiting);

  // Keep the status fresh while a run is going, whether this page started it or the daily job did
  useEffect(() => {
    if (!busy) return undefined;
    const id = setInterval(checkProgress, POLL_MS);
    return () => clearInterval(id);
  }, [busy, checkProgress]);

  const handleSync = useCallback(async () => {
    if (busy) return;
    setNotice(null);
    const before = await fetchSyncStatus();
    if (!before) {
      setNotice({ severity: 'error', text: 'Could not read the sync status, so the sync was not started.' });
      return;
    }
    pendingRef.current = { baseline: progressMarker(before), startedAt: Date.now() };
    setWaiting(true);
    try {
      await triggerBriefingPerformanceSync({});
    } catch (err) {
      pendingRef.current = null;
      setWaiting(false);
      setNotice({ severity: 'error', text: err?.message || 'Failed to start the Datadog sync. Please try again.' });
    }
  }, [busy, fetchSyncStatus]);

  const liveSkipped = clippedMonths(syncStatus?.skippedClipped);
  const lastSkipped = clippedMonths(syncStatus?.lastSkippedClipped);
  const skippedLabel = liveSkipped.length
    ? `Skipping clipped: ${liveSkipped.join(', ')}`
    : (lastSkipped.length ? `Last skipped clipped: ${lastSkipped.join(', ')}` : null);
  const skippedTitle = liveSkipped.length
    ? clippedTooltip(syncStatus.skippedClipped)
    : clippedTooltip(syncStatus?.lastSkippedClipped);

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, color: darkGreenColor }}>
        Client Performance sync
      </Typography>

      <Paper elevation={0} sx={{ p: 3, borderRadius: 2, border: '1px solid #e0e0e0' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SpeedIcon sx={{ mr: 1, color: darkGreenColor }} />
          <Typography variant="h6" sx={{ color: darkGreenColor }}>
            Briefing Client Performance (Datadog → Mongo)
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pulls Apache stats from Datadog into monthly Mongo rollups used by customer briefing
          Client Performance tabs. Uses server Datadog env keys. A daily job also runs at 03:00 UTC.
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', mb: 1 }}>
          <Tooltip title="Pull recent Datadog Apache stats into Mongo monthly rollups (server env keys).">
            <span>
              <Button
                variant="outlined"
                startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <SyncIcon />}
                onClick={handleSync}
                disabled={busy}
                sx={{
                  borderColor: darkGreenColor,
                  color: darkGreenColor,
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { borderColor: '#0f2e1f', backgroundColor: 'rgba(29, 68, 52, 0.04)' }
                }}
              >
                {busy ? 'Syncing…' : 'Sync'}
              </Button>
            </span>
          </Tooltip>
        </Box>

        {notice ? (
          <Alert severity={notice.severity} sx={{ mt: 2 }} onClose={() => setNotice(null)}>
            {notice.text}
          </Alert>
        ) : null}

        {syncStatus ? (
          <Box
            role="status"
            sx={{
              mt: 2,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1.5
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: '50%',
                  bgcolor: busy ? '#f59e0b' : (syncStatus.lastError ? '#d32f2f' : '#4caf50'),
                  flexShrink: 0
                }}
              />
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {busy
                  ? `Syncing… ${syncStatus.progress?.done ?? 0} / ${syncStatus.progress?.total ?? '?'} months`
                  : 'Daily auto-sync scheduled (03:00 UTC)'}
              </Typography>
            </Box>
            {syncStatus.lastSyncAt ? (
              <Typography variant="caption" color="text.secondary">
                Last synced:{' '}
                <strong>{formatAsOf(syncStatus.lastSyncAt)}</strong>
                {syncStatus.lastSyncCount != null ? ` — ${plural(syncStatus.lastSyncCount, 'month')}` : ''}
                {syncStatus.lastSyncErrors > 0 ? `, ${plural(syncStatus.lastSyncErrors, 'error')}` : ''}
              </Typography>
            ) : null}
            {syncStatus.nextSyncAt && !busy ? (
              <Tooltip title="Runs daily at 03:00 UTC">
                <Typography variant="caption" color="text.secondary" sx={{ cursor: 'help' }}>
                  Next sync: <strong>{formatAsOf(syncStatus.nextSyncAt)}</strong>
                </Typography>
              </Tooltip>
            ) : null}
            {syncStatus.totalMonths > 0 ? (
              <Chip
                size="small"
                label={`${syncStatus.totalMonths.toLocaleString()} months stored`}
                sx={{ fontSize: 10, height: 18 }}
              />
            ) : null}
            {skippedLabel ? (
              <Tooltip title={skippedTitle || ''}>
                <Typography variant="caption" color="text.secondary" sx={{ cursor: skippedTitle ? 'help' : 'default' }}>
                  {skippedLabel}
                </Typography>
              </Tooltip>
            ) : null}
            {syncStatus.lastError && !busy ? (
              <Typography variant="caption" color="error">
                Last problem ({formatAsOf(syncStatus.lastError.at)}): {syncStatus.lastError.message}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </Paper>

      <Box sx={{ mt: 3 }}>
        <BriefingPerformanceFilterAdmin />
      </Box>
    </Box>
  );
};

export default BriefingPerformanceSyncAdmin;
